/**
 * TextEntriesManager — replaces the dialog-based Manual Text Entries
 * card in the Admin Portal with a fast quick-add + bulk-import flow.
 *
 * Design goals (all direct pain points the admin surfaced):
 *   1. "Hard to add data / refresh a lot" — inline form always visible.
 *      Save → form resets → focus returns to Title → list reloads on
 *      the same page, no dialog dance.
 *   2. "Want to see what entries are in the KB" — compact table with
 *      title, category, chunk footprint, enabled toggle, edit, delete.
 *      Search box + category filter chips at the top.
 *   3. "Categories that help courses select from the right section" —
 *      dropdown of curated categories (still allows free-text override).
 *      Category chips show live counts so admins see thin coverage.
 *   4. "Have many to add" — Bulk import button opens a modal for
 *      pasting JSON. Backend accepts up to 500 rows per batch.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Trash2, Edit3, Upload, X, BookOpen, Search, Sparkles, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

// Curated defaults so admins get consistent tagging.
// Free-text still allowed via the "custom" option.
const CURATED_CATEGORIES = [
  'Freeze Drying Science',
  'Business',
  'Equipment',
  'Recipes',
  'Safety',
  'Troubleshooting',
  'Nutrition',
  'Storage',
  'Marketing',
  'General',
];

const EMPTY_FORM = { title: '', content: '', category: 'General', enabled: true };

export default function TextEntriesManager() {
  const { API } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexProgress, setReindexProgress] = useState(null); // {done, total, chunks, skipped, errors}
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customCategory, setCustomCategory] = useState(false);
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total, created, skipped, failed }

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/ai-knowledge`),
        axios.get(`${API}/admin/ai-knowledge/stats`),
      ]);
      setEntries(listRes.data || []);
      setStats(statsRes.data || null);
    } catch {
      toast.error('Failed to load text entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Bulk reindex — walks every enabled entry and re-embeds it into
  // ai_knowledge_chunks. Idempotent. Necessary for admins whose entries
  // were created before the auto-reindex pipeline existed OR whose embed
  // jobs silently failed (both are why the "Enabled + Embedded" count
  // was lower than "Enabled").
  const reindexAll = async () => {
    if (reindexing) return;
    const enabledN = stats?.enabled_entries ?? 0;
    if (enabledN === 0) { toast.info('No enabled entries to reindex.'); return; }
    if (!window.confirm(
      `Re-embed all ${enabledN} enabled text entries?\n\n` +
      'This makes them AI-searchable for lesson citations, Ask an Expert, and auto-cite. ' +
      'Runs serially — it can take a minute for large libraries.'
    )) return;
    setReindexing(true);
    // Chunked loop — POST with limit=25, then keep advancing offset
    // until has_more=false. Live progress bar reads reindexProgress.
    const CHUNK = 25;
    let offset = 0;
    let totals = { done: 0, total: enabledN, chunks: 0, skipped: 0, errors: 0 };
    setReindexProgress(totals);
    const t = toast.loading(`Reindexing 0 / ${enabledN} entries…`);
    try {
      while (true) {
        const { data } = await axios.post(
          `${API}/admin/ai-knowledge/reindex-text-entries`,
          null,
          { params: { offset, limit: CHUNK } },
        );
        totals = {
          done:    totals.done + (data.processed || 0),
          total:   data.total ?? totals.total,
          chunks:  totals.chunks + (data.chunks_written || 0),
          skipped: totals.skipped + (data.skipped || 0),
          errors:  totals.errors + (data.errors || 0),
        };
        setReindexProgress(totals);
        toast.loading(`Reindexing ${totals.done} / ${totals.total} entries…`, { id: t });
        if (!data.has_more) break;
        offset = data.next_offset;
        // Yield so the UI can paint the progress bar between chunks.
        await new Promise((r) => setTimeout(r, 30));
      }
      const msg = `Reindexed ${totals.done} entries → ${totals.chunks} chunks` +
        (totals.skipped ? ` · ${totals.skipped} skipped (too short / empty)` : '') +
        (totals.errors ? ` · ${totals.errors} errors` : '');
      toast.success(msg, { id: t, duration: 8000 });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Reindex failed';
      toast.error(`Reindex failed: ${detail}`, { id: t, duration: 8000 });
    } finally {
      setReindexing(false);
      // Keep the completed bar visible briefly, then clear.
      setTimeout(() => setReindexProgress(null), 2000);
    }
  };

  const save = async () => {
    const title = form.title.trim();
    const content = form.content.trim();
    if (!title || content.length < 10) {
      toast.error('Title required and content must be at least 10 chars');
      return;
    }
    setSaving(true);
    try {
      const payload = { title, content, category: form.category.trim() || 'General', enabled: form.enabled };
      if (editingId) {
        await axios.put(`${API}/admin/ai-knowledge/${editingId}`, payload);
        toast.success('Entry updated — re-embedding in the background');
      } else {
        await axios.post(`${API}/admin/ai-knowledge`, payload);
        toast.success('Entry added — auto-embedding into the KB');
      }
      setForm({ ...EMPTY_FORM, category: form.category });   // keep last-used category
      setEditingId(null);
      setCustomCategory(false);
      // Move focus back to title for rapid entry
      setTimeout(() => {
        const el = document.querySelector('[data-testid="te-title-input"]');
        if (el) el.focus();
      }, 100);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ title: e.title, content: e.content, category: e.category || 'General', enabled: e.enabled !== false });
    setCustomCategory(!CURATED_CATEGORIES.includes(e.category));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCustomCategory(false);
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? The KB chunks will be purged automatically.`)) return;
    try {
      await axios.delete(`${API}/admin/ai-knowledge/${id}`);
      toast.success('Entry deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const runBulk = async () => {
    let rows;
    try {
      rows = JSON.parse(bulkText);
      if (!Array.isArray(rows)) throw new Error('Expected a JSON array of {title, content, category}');
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
      return;
    }
    if (rows.length === 0) {
      toast.error('Empty array');
      return;
    }
    // Normalize common field aliases so users can paste JSON exports from
    // sheets/docs that use PascalCase or alternate content keys.
    const pick = (obj, keys) => {
      for (const k of keys) {
        if (obj && typeof obj[k] === 'string' && obj[k].trim()) return obj[k];
      }
      return '';
    };
    const normalized = rows.map((r) => ({
      title: pick(r, ['title', 'Title', 'name', 'Name']),
      content: pick(r, ['content', 'Content', 'description', 'Description', 'body', 'Body', 'text', 'Text']),
      category: pick(r, ['category', 'Category', 'topic', 'Topic']) || 'Uncategorized',
      enabled: r?.enabled ?? true,
    }));

    // Chunk client-side. 200 entries in one request tends to overrun
    // CDN/proxy idle timeouts on production, so we send small batches
    // serially with live progress. Each batch also triggers reindex tasks
    // on the server for its own rows.
    const CHUNK_SIZE = 25;
    const chunks = [];
    for (let i = 0; i < normalized.length; i += CHUNK_SIZE) {
      chunks.push(normalized.slice(i, i + CHUNK_SIZE));
    }

    setBulkSaving(true);
    setBulkProgress({ done: 0, total: normalized.length, created: 0, skipped: 0, failed: 0, failedDetails: [] });

    let totalCreated = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    const failedDetails = [];

    for (let idx = 0; idx < chunks.length; idx++) {
      const batch = chunks[idx];
      try {
        const { data } = await axios.post(
          `${API}/admin/ai-knowledge/bulk`,
          batch,
          { timeout: 60000 }, // 60s per chunk of 25 is generous
        );
        totalCreated += data?.created_count || 0;
        totalSkipped += data?.skipped_count || 0;
      } catch (e) {
        // Whole batch failed (network, 5xx, timeout). Count all rows as failed
        // but keep going — no reason to lose the rest of the import.
        totalFailed += batch.length;
        const detail = e?.response?.data?.detail;
        const msg =
          typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join('; ')
          : e?.code === 'ECONNABORTED' ? 'Request timed out'
          : e?.message || 'Unknown error';
        failedDetails.push({ chunk: idx + 1, size: batch.length, msg });
      }
      setBulkProgress({
        done: Math.min((idx + 1) * CHUNK_SIZE, normalized.length),
        total: normalized.length,
        created: totalCreated,
        skipped: totalSkipped,
        failed: totalFailed,
        failedDetails,
      });
    }

    setBulkSaving(false);

    if (totalFailed === 0) {
      toast.success(
        `Imported ${totalCreated}. ${totalSkipped} skipped.`,
        { duration: 8000 },
      );
      setBulkOpen(false);
      setBulkText('');
      setBulkProgress(null);
      load();
    } else {
      toast.error(
        `Imported ${totalCreated}. ${totalFailed} failed in ${failedDetails.length} batch${failedDetails.length === 1 ? '' : 'es'}. See details in modal.`,
        { duration: 10000 },
      );
      load();
    }
  };

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    return entries.filter((e) => {
      if (categoryFilter && (e.category || 'Uncategorized') !== categoryFilter) return false;
      if (qq && !`${e.title} ${e.content} ${e.category}`.toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [entries, q, categoryFilter]);

  const categoryOptions = useMemo(() => {
    // Merge curated + any custom categories currently in use
    const used = new Set(entries.map((e) => e.category).filter(Boolean));
    const merged = [...CURATED_CATEGORIES];
    used.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
    return merged;
  }, [entries]);

  return (
    <>
      <Card data-testid="text-entries-manager">
        <CardHeader>
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Manual Text Entries
              </CardTitle>
              <CardDescription>
                Short-form facts + reference snippets. Auto-embedded into the AI Knowledge Base
                on save. Categorise them so the AI can bias toward the right library section
                when answering course-specific questions.
              </CardDescription>
              {stats ? (() => {
                const embedded = stats.embedded_entries ?? 0;
                const enabled = stats.enabled_entries ?? 0;
                const unindexed = Math.max(0, enabled - embedded);
                const allHealthy = unindexed === 0 && enabled > 0;
                return (
                  <div className="mt-1 space-y-1" data-testid="te-summary">
                    <p className="text-[11px] text-slate-500">
                      <strong>{stats.total_entries}</strong> entries · <strong>{enabled}</strong> enabled ·
                      {' '}<strong>{stats.total_chunks}</strong> KB chunks
                    </p>
                    {/* AI-search health — the count that actually matters
                        for citations / suggest / auto-cite / Ask Expert.
                        When lower than "enabled", the reindex button
                        below is the cure. */}
                    <p
                      className={`text-[11px] font-semibold inline-flex items-center gap-1 ${
                        allHealthy
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : unindexed > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500'
                      }`}
                      data-testid="te-embedded-health"
                    >
                      {allHealthy
                        ? <CheckCircle2 className="w-3 h-3" />
                        : <AlertTriangle className="w-3 h-3" />}
                      <strong>{embedded}</strong>/<strong>{enabled}</strong> AI-searchable
                      {unindexed > 0 ? ` — ${unindexed} still need embedding` : ' — every entry is ready to cite'}
                    </p>
                    {/* Live progress bar — only visible during bulk reindex.
                        Shows done/total + chunk count so admins with 1000+
                        entries see steady forward motion instead of one
                        opaque spinner. */}
                    {reindexProgress && (
                      <div className="mt-2 max-w-md" data-testid="te-reindex-progress">
                        <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
                          <span className="text-primary">Reindexing…</span>
                          <span className="tabular-nums text-muted-foreground">
                            {reindexProgress.done} / {reindexProgress.total}
                            {reindexProgress.chunks ? ` · ${reindexProgress.chunks} chunks` : ''}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${Math.min(100, Math.round(100 * reindexProgress.done / Math.max(1, reindexProgress.total)))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })() : null}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={reindexAll}
                disabled={reindexing || !stats?.enabled_entries}
                title="Re-embed every enabled entry so they show up in citations, suggest, and Ask Expert"
                data-testid="te-reindex-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${reindexing ? 'animate-spin' : ''}`} />
                {reindexing ? 'Reindexing…' : 'Reindex all'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="te-bulk-open">
                <Upload className="w-4 h-4 mr-2" /> Bulk import
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Quick-add / edit form — always visible */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/60 dark:bg-slate-800/40 space-y-3" data-testid="te-form">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                {editingId ? 'Edit entry' : 'Add new entry'}
              </h4>
              {editingId ? (
                <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="te-cancel-edit">
                  Cancel edit
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Sublimation temperature range"
                  data-testid="te-title-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                {!customCategory ? (
                  <div className="flex gap-2">
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="flex-1 h-9 px-2 rounded border border-slate-300 dark:border-slate-700 bg-background text-sm"
                      data-testid="te-category-select"
                    >
                      {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => setCustomCategory(true)} data-testid="te-category-custom">
                      Custom…
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder="Custom category…"
                      data-testid="te-category-input"
                    />
                    <Button variant="ghost" size="sm" onClick={() => { setCustomCategory(false); setForm({ ...form, category: 'General' }); }}>
                      List
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                rows={6}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Detailed info the AI can quote (min 10 chars). Cmd/Ctrl-Enter to save."
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); }
                }}
                data-testid="te-content-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">{form.content.length} chars · Cmd/Ctrl-Enter to save</p>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="te-enabled-checkbox"
                />
                Enabled (appears in AI retrieval)
              </label>
              <Button onClick={save} disabled={saving} data-testid="te-save-btn">
                {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add entry')}
              </Button>
            </div>
          </div>

          {/* Category filter chips */}
          {stats?.by_category?.length ? (
            <div className="flex flex-wrap gap-2 items-center" data-testid="te-category-chips">
              <button
                type="button"
                onClick={() => setCategoryFilter(null)}
                className={`text-xs px-2 py-1 rounded-full border ${!categoryFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}
                data-testid="te-filter-all"
              >
                All ({stats.total_entries})
              </button>
              {stats.by_category.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  onClick={() => setCategoryFilter(c.category)}
                  className={`text-xs px-2 py-1 rounded-full border ${categoryFilter === c.category ? 'bg-primary text-primary-foreground border-primary' : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}
                  data-testid={`te-filter-${c.category.replaceAll(' ', '-').toLowerCase()}`}
                >
                  {c.category} ({c.count})
                </button>
              ))}
            </div>
          ) : null}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, content, or category…"
              className="pl-9"
              data-testid="te-search"
            />
          </div>

          {/* Entry list */}
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              {entries.length === 0 ? 'No entries yet — add your first one above.' : 'No entries match this filter.'}
            </p>
          ) : (
            <div className="space-y-2" data-testid="te-list">
              {filtered.map((e) => {
                const chunks = stats?.chunks_by_id?.[e.id] ?? 0;
                return (
                  <div
                    key={e.id}
                    className={`rounded-lg border p-3 ${e.enabled === false ? 'border-slate-200 bg-slate-100/40 dark:bg-slate-800/40 opacity-60' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}
                    data-testid={`te-row-${e.id}`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-sm truncate">{e.title}</h4>
                          <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 px-1.5 py-0.5 rounded">
                            {e.category || 'Uncategorized'}
                          </span>
                          {e.enabled === false ? (
                            <span className="text-[10px] uppercase tracking-wide bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 px-1.5 py-0.5 rounded">
                              Disabled
                            </span>
                          ) : null}
                          <span className="text-[10px] text-slate-500">{chunks} chunk{chunks === 1 ? '' : 's'}</span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{e.content}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(e)} data-testid={`te-edit-${e.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(e.id, e.title)} data-testid={`te-delete-${e.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk import modal */}
      {bulkOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur" onClick={() => setBulkOpen(false)} data-testid="te-bulk-modal">
          <div className="bg-background border border-border rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Bulk import</div>
                <h3 className="text-lg font-bold mt-1">Paste a JSON array</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Up to 500 entries per batch. Each entry needs a <code>title</code> (or <code>Title</code>) and <code>content</code> (or <code>Content</code>/<code>Description</code>/<code>Body</code>). <code>category</code>/<code>Category</code> optional.
                </p>
              </div>
              <button type="button" onClick={() => setBulkOpen(false)} className="text-slate-400 hover:text-foreground" data-testid="te-bulk-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <Textarea
                rows={bulkProgress ? 8 : 16}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={`[\n  {"title": "Sublimation basics", "content": "Sublimation is…", "category": "Freeze Drying Science"},\n  {"title": "Pricing strategy", "content": "Charge based on…", "category": "Business"}\n]`}
                className="font-mono text-xs"
                data-testid="te-bulk-textarea"
                disabled={bulkSaving}
              />
              <p className="text-[11px] text-slate-500">{bulkText.length.toLocaleString()} chars</p>

              {/* Live progress + failure details ------------------------- */}
              {bulkProgress ? (
                <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 space-y-2" data-testid="te-bulk-progress">
                  <div className="flex justify-between items-center text-xs font-semibold">
                    <span>
                      {bulkSaving ? 'Importing' : 'Import finished'} —{' '}
                      {bulkProgress.done}/{bulkProgress.total}
                    </span>
                    <span className="text-slate-500">
                      {bulkProgress.created} imported · {bulkProgress.skipped} skipped
                      {bulkProgress.failed ? ` · ${bulkProgress.failed} failed` : ''}
                    </span>
                  </div>
                  <div className="h-2 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div
                      className={`h-full transition-all ${bulkProgress.failed ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{
                        width: `${bulkProgress.total ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}%`,
                      }}
                    />
                  </div>
                  {bulkProgress.failedDetails && bulkProgress.failedDetails.length > 0 ? (
                    <div className="text-[11px] text-rose-700 dark:text-rose-400 space-y-0.5 max-h-24 overflow-auto">
                      {bulkProgress.failedDetails.map((f, i) => (
                        <div key={i}>
                          Batch {f.chunk} ({f.size} entries): {f.msg}
                        </div>
                      ))}
                      <div className="text-slate-500">
                        Tip: keep the modal open, remove the entries that landed, and retry only what failed.
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setBulkOpen(false); setBulkProgress(null); }}
                disabled={bulkSaving}
              >
                {bulkProgress && !bulkSaving ? 'Close' : 'Cancel'}
              </Button>
              <Button onClick={runBulk} disabled={bulkSaving || !bulkText.trim()} data-testid="te-bulk-run">
                <Upload className="w-4 h-4 mr-2" />
                {bulkSaving
                  ? (bulkProgress ? `Importing ${bulkProgress.done}/${bulkProgress.total}…` : 'Importing…')
                  : (bulkProgress?.failed ? 'Retry import' : 'Import batch')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
